<template>
  <MaintenanceScreen v-if="appStatusStore.isBackendUnavailable" />
  <div v-else id="app-container" :class="{ 'auth-view': !userStore.isAuthenticated }">
    <a href="#main-content" class="skip-link">{{ t('a11y.skipToContent') }}</a>
    <NotificationCenter />

    <TopNav v-if="shouldShowNav" />
    <WalletAccessBanBanner v-if="shouldShowNav" />

    <div class="main-content-area" :class="{
      'auth-layout': !shouldShowNav,
      'has-bottom-nav': shouldShowNav && isMobile
    }">
      <main id="main-content" class="main-view" tabindex="-1">
        <RouterView />
      </main>
      <AppFooter v-if="shouldShowNav" />
      <div v-else-if="showAuthLicenseBar" class="auth-license-bar">{{ t('nbkrLicense') }}</div>
    </div>

    <WalletTransferBanNotice v-if="userStore.isAuthenticated" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { applyRouteTitle } from '@/router'
import { useUserStore } from '@/stores/user'
import { useThemeStore } from '@/stores/theme'
import { useAccessibilityStore } from '@/stores/accessibility'
import { useAppStatusStore } from '@/stores/appStatus'
import { useI18n } from '@/composables/useI18n'
import { useIsMobile } from '@/composables/useIsMobile'
import TopNav from '@/components/TopNav.vue'
import AppFooter from '@/components/AppFooter.vue'
import NotificationCenter from '@/components/NotificationCenter.vue'
import WalletTransferBanNotice from '@/components/WalletTransferBanNotice.vue'
import WalletAccessBanBanner from '@/components/WalletAccessBanBanner.vue'
import MaintenanceScreen from '@/components/MaintenanceScreen.vue'

const route = useRoute()
const userStore = useUserStore()
const appStatusStore = useAppStatusStore()
const themeStore = useThemeStore()
const accessibilityStore = useAccessibilityStore()
const { t, locale, initializeLocale, setUserLanguage } = useI18n()

const isInitializing = ref(true)
const isMobile = useIsMobile(48)

watch(
  () => route.fullPath,
  async () => {
    await nextTick()
    const main = document.getElementById('main-content')
    if (main && !document.activeElement?.closest?.('[role="dialog"]')) {
      main.focus({ preventScroll: true })
    }
  }
)

watch(locale, () => {
  applyRouteTitle(route)
})

watch(() => userStore.user?.language, (newLanguage, oldLanguage) => {
  if (!newLanguage) return

  // При первом получении данных пользователя (логин / обновление страницы)
  // приоритет у локально выбранного языка — он мог быть задан до входа
  if (oldLanguage === undefined) {
    const savedLocale = localStorage.getItem('locale')
    if (savedLocale && savedLocale !== newLanguage) {
      setUserLanguage(savedLocale)
      userStore.updateLanguage(savedLocale)
      return
    }
  }

  setUserLanguage(newLanguage)
}, { immediate: true })

const pagesWithoutLayout = ['/login', '/register', '/reset-password', '/verify-email', '/', '/checkout', '/docs/inbound']

const shouldShowNav = computed(() => userStore.isAuthenticated && !pagesWithoutLayout.includes(route.path) && !route.path.startsWith('/checkout'))

const pagesWithLicenseInPanel = ['/login', '/register']
const showAuthLicenseBar = computed(() => route.path !== '/' && !pagesWithLicenseInPanel.includes(route.path) && !route.path.startsWith('/docs'))

onMounted(async () => {
  try {
    themeStore.initTheme()
    accessibilityStore.init()
    initializeLocale()
    await userStore.init()
  } catch (error) {
    console.error('Initialization error:', error)
  } finally {
    isInitializing.value = false
  }
})

</script>

<style scoped>
.skip-link {
  position: absolute;
  left: 0.75rem;
  top: 0.75rem;
  z-index: 10000;
  padding: 0.75rem 1rem;
  background: var(--primary-blue);
  color: #0b0e11;
  font-weight: 700;
  border-radius: var(--radius-md);
  transform: translateY(-200%);
  transition: transform var(--transition-fast);
}

.skip-link:focus {
  transform: translateY(0);
  outline: 3px solid var(--text-primary);
  outline-offset: 2px;
}

.main-view:focus {
  outline: none;
}

#app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--background-primary);
}

.main-content-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-x: clip;
}

.main-content-area.auth-layout {
  border-radius: 0 !important;
}

.main-content-area.has-bottom-nav {
  padding-bottom: calc(4.25rem + env(safe-area-inset-bottom, 0));
}

.main-view {
  flex: 1;
  padding: 0;
  overflow-y: auto;
  overflow-x: clip;
  min-height: 0;
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.auth-license-bar {
  padding: 0.75rem 1rem;
  font-size: 0.75rem;
  color: var(--text-tertiary);
  text-align: center;
  border-top: 1px solid var(--border-primary);
  background: var(--background-secondary);
}

@media (max-width: 768px) {
  .auth-license-bar {
    font-size: 0.7rem;
    padding: 0.6rem 0.75rem;
  }
}
</style> 